import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { ArrowUpToLine } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { AUDIO_LIMITS, IMAGE_LIMITS, THUMBNAIL_LIMITS, VIDEO_LIMITS } from "@/constants/media"
import { nativeVideoStatusSuffix } from "@/features/rituals/utils/nativeVideoStatusLabel"
import {
  buildAcceptAttribute,
  isFileTypeAllowed,
  looksLikeAudioFile,
  looksLikeImageFile,
  looksLikeVideoFile,
} from "@/utils/fileTypes"
import { AIGeneratedIcon } from "../icons"
import { AiImageGenerator, type EntityContext } from "./AiImageGenerator"
import { ImageCropperModal } from "./ImageCropperModal"
import { MediaUploadModal } from "./MediaUploadModal"
import { MediaPreview } from "./mediaPreview"

const DEFAULT_MAX_FILES = 10
const DEFAULT_MAX_IMAGE_FILE_SIZE = IMAGE_LIMITS.MAX_FILE_SIZE
const DEFAULT_MAX_VIDEO_FILE_SIZE = VIDEO_LIMITS.MAX_FILE_SIZE
const DEFAULT_MAX_AUDIO_FILE_SIZE = AUDIO_LIMITS.MAX_FILE_SIZE
const DEFAULT_ALLOWED_IMAGE_TYPES = [...IMAGE_LIMITS.ALLOWED_TYPES]
const DEFAULT_ALLOWED_VIDEO_TYPES = [...VIDEO_LIMITS.ALLOWED_TYPES]
const DEFAULT_ALLOWED_AUDIO_TYPES = [...AUDIO_LIMITS.ALLOWED_TYPES]

export interface LocalMediaItem {
  id: string
  uploadId?: string
  src?: string
  file?: File
  mediaType: "IMAGE" | "VIDEO" | "AUDIO"
  isCover: boolean
  sortOrder: number
  title: string | null
  videoTitle: string | null
  thumbnailSrc?: string | null
  thumbnailFile?: File
  thumbnailUploadId?: string | null
}

export interface MediaChanges {
  mediaItems: LocalMediaItem[]
  deletedMediaIds: string[]
  newFiles: File[]
  coverMediaId: string | null
  orderedMediaIds: string[]
  videoTitles: Record<string, string>
  thumbnailFiles: Record<string, File>
}

export interface NativeVideoItem {
  localId: string
  status: string
  filename: string
  /** User-specified display title. Defaults to filename. */
  title: string
  thumbnailUrl: string | null
  sortOrder: number
  uploadedBytes: number
  totalBytes: number
}

interface MediaUploadSectionProps {
  initialMedia?: LocalMediaItem[]
  onMediaChange?: (changes: MediaChanges) => void
  onCoverImageChange?: (hasCover: boolean) => void
  onReplaceImage?: (
    uploadId: string,
    croppedFile: File,
  ) => Promise<{ id: string; filePath: string; isCover: boolean } | undefined>
  showVideoSection?: boolean
  showAudioSection?: boolean
  maxFiles?: number
  maxImageFileSize?: number
  maxVideoFileSize?: number
  maxAudioFileSize?: number
  allowedImageTypes?: string[]
  allowedVideoTypes?: string[]
  allowedAudioTypes?: string[]
  enableAiGeneration?: boolean
  aiEntityType?: "ritual" | "community" | "coaching"
  aiEntityContext?: EntityContext
  isUploading?: boolean
  nativeVideos?: NativeVideoItem[]
  isVideoFile?: (file: File) => boolean
  onUploadVideo?: (file: File) => void
  onDeleteVideo?: (localId: string) => void
  onNativeVideoTitleChange?: (localId: string, title: string) => void
  onNativeVideoThumbnailChange?: (localId: string, file: File) => void
  onUploadAudioCover?: (args: {
    ritualId: string
    uploadId: string
    file: File
  }) => Promise<unknown>
  ritualId?: string
}

const NATIVE_VIDEO_ID_PREFIX = "cfvideo-"

function SortableMediaItem({
  item,
  onSetCover,
  onDelete,
  onEditImage,
  onTitleChange,
  onThumbnailSelect,
  onAudioPosterSelect,
  coverImageUrl,
}: {
  item: LocalMediaItem
  onSetCover: (id: string) => void
  onDelete: (id: string) => void
  onEditImage: (item: LocalMediaItem) => void
  onTitleChange: (id: string, title: string) => void
  onThumbnailSelect: (id: string) => void
  onAudioPosterSelect?: (id: string) => void
  coverImageUrl?: string
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  })

  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [draftTitle, setDraftTitle] = useState(item.title || "")

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  useEffect(() => {
    setDraftTitle(item.title || "")
  }, [item.title])

  const subtitle =
    item.mediaType === "VIDEO"
      ? "MP4"
      : item.mediaType === "AUDIO"
        ? "Audio"
        : (() => {
            const ext =
              item.file?.name?.split(".").pop()?.toUpperCase() ||
              item.src?.split(".").pop()?.toUpperCase()
            return ext || "Image"
          })()

  const displayTitle =
    item.mediaType === "IMAGE"
      ? "Image"
      : item.title || item.file?.name?.replace(/\.[^.]+$/, "") || "Video"

  const handleStartEditTitle = () => {
    setDraftTitle(item.title || "")
    setIsEditingTitle(true)
  }

  const handleFinishEditTitle = () => {
    setIsEditingTitle(false)
    if (draftTitle.trim() !== (item.title || "")) {
      onTitleChange(item.id, draftTitle.trim())
    }
  }

  const canEditTitle = item.mediaType === "VIDEO" || item.mediaType === "AUDIO"

  return (
    <div ref={setNodeRef} style={style}>
      <MediaPreview
        type={item.mediaType === "IMAGE" ? "image" : item.mediaType === "AUDIO" ? "audio" : "video"}
        src={item.src}
        thumbnailSrc={item.thumbnailSrc ?? undefined}
        coverImageUrl={coverImageUrl}
        title={isEditingTitle && canEditTitle ? undefined : displayTitle}
        subtitle={subtitle}
        isCover={item.isCover}
        isDragging={isDragging}
        layout="row"
        editingTitle={isEditingTitle && canEditTitle ? draftTitle : undefined}
        onEditingTitleChange={isEditingTitle && canEditTitle ? setDraftTitle : undefined}
        onEditTitleSubmit={isEditingTitle && canEditTitle ? handleFinishEditTitle : undefined}
        onEditTitleCancel={
          isEditingTitle && canEditTitle
            ? () => {
                setDraftTitle(item.title || "")
                setIsEditingTitle(false)
              }
            : undefined
        }
        dragHandleProps={{ ...attributes, ...listeners }}
        onCoverSelect={
          item.mediaType === "IMAGE" && !item.isCover ? () => onSetCover(item.id) : undefined
        }
        onRemove={() => onDelete(item.id)}
        onEditImage={item.mediaType === "IMAGE" ? () => onEditImage(item) : undefined}
        onChange={canEditTitle ? handleStartEditTitle : undefined}
        onEditThumbnail={
          item.mediaType === "VIDEO"
            ? () => onThumbnailSelect(item.id)
            : item.mediaType === "AUDIO"
              ? () => onAudioPosterSelect?.(item.id)
              : undefined
        }
      />
    </div>
  )
}

export function MediaUploadSection({
  initialMedia,
  onMediaChange,
  onCoverImageChange,
  onReplaceImage,
  showVideoSection = true,
  showAudioSection = false,
  maxFiles = DEFAULT_MAX_FILES,
  maxImageFileSize = DEFAULT_MAX_IMAGE_FILE_SIZE,
  maxVideoFileSize = DEFAULT_MAX_VIDEO_FILE_SIZE,
  maxAudioFileSize = DEFAULT_MAX_AUDIO_FILE_SIZE,
  allowedImageTypes = DEFAULT_ALLOWED_IMAGE_TYPES,
  allowedVideoTypes = DEFAULT_ALLOWED_VIDEO_TYPES,
  allowedAudioTypes = DEFAULT_ALLOWED_AUDIO_TYPES,
  enableAiGeneration = false,
  aiEntityType,
  aiEntityContext,
  isUploading,
  nativeVideos = [],
  isVideoFile,
  onUploadVideo,
  onDeleteVideo,
  onNativeVideoTitleChange,
  onNativeVideoThumbnailChange,
  onUploadAudioCover,
  ritualId,
}: MediaUploadSectionProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const thumbnailInputRef = useRef<HTMLInputElement>(null)
  const audioPosterInputRef = useRef<HTMLInputElement>(null)
  const pendingThumbnailIdRef = useRef<string | null>(null)

  const [uploadModalOpen, setUploadModalOpen] = useState(false)
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false)
  const [croppingItem, setCroppingItem] = useState<LocalMediaItem | null>(null)
  const [editingThumbnailItem, setEditingThumbnailItem] = useState<LocalMediaItem | null>(null)

  const [mediaItems, setMediaItems] = useState<LocalMediaItem[]>([])
  const [deletedMediaIds, setDeletedMediaIds] = useState<string[]>([])
  const [thumbnailFiles, setThumbnailFiles] = useState<Record<string, File>>({})

  const hasSynced = useRef(false)
  useEffect(() => {
    if (initialMedia && initialMedia.length > 0 && !hasSynced.current) {
      setMediaItems(initialMedia)
      hasSynced.current = true
    }
  }, [initialMedia])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const notifyParent = useCallback(
    (items: LocalMediaItem[], deleted: string[], thumbs: Record<string, File>) => {
      if (!onMediaChange) return
      const coverItem = items.find(i => i.isCover)
      const newFiles: File[] = []
      const orderedMediaIds: string[] = []
      const videoTitles: Record<string, string> = {}
      for (const item of items) {
        orderedMediaIds.push(item.id)
        if (item.file) newFiles.push(item.file)
        if ((item.mediaType === "VIDEO" || item.mediaType === "AUDIO") && item.videoTitle) {
          videoTitles[item.id] = item.videoTitle
        }
      }
      const changes: MediaChanges = {
        mediaItems: items,
        deletedMediaIds: deleted,
        newFiles,
        coverMediaId: coverItem?.id || null,
        orderedMediaIds,
        videoTitles,
        thumbnailFiles: thumbs,
      }
      queueMicrotask(() => onMediaChange(changes))
    },
    [onMediaChange],
  )

  useEffect(() => {
    const hasCover = mediaItems.some(i => i.isCover && !deletedMediaIds.includes(i.id))
    onCoverImageChange?.(hasCover)
  }, [mediaItems, deletedMediaIds, onCoverImageChange])

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      setMediaItems(prev => {
        const reordered = arrayMove(
          prev,
          prev.findIndex(i => i.id === active.id),
          prev.findIndex(i => i.id === over.id),
        )
        reordered.forEach((item, idx) => {
          item.sortOrder = idx
        })
        notifyParent(reordered, deletedMediaIds, thumbnailFiles)
        return [...reordered]
      })
    },
    [deletedMediaIds, thumbnailFiles, notifyParent],
  )

  const handleSetCover = useCallback(
    (id: string) => {
      setMediaItems(prev => {
        const updated = prev.map(i => ({ ...i, isCover: i.id === id }))
        notifyParent(updated, deletedMediaIds, thumbnailFiles)
        return updated
      })
    },
    [deletedMediaIds, thumbnailFiles, notifyParent],
  )

  const handleDelete = useCallback(
    (id: string) => {
      if (id.startsWith(NATIVE_VIDEO_ID_PREFIX) && onDeleteVideo) {
        void onDeleteVideo(id.slice(NATIVE_VIDEO_ID_PREFIX.length))
        return
      }

      const item = mediaItems.find(i => i.id === id)
      if (!item) return

      const images = mediaItems.filter(
        i => i.mediaType === "IMAGE" && i.id !== id && !deletedMediaIds.includes(i.id),
      )
      if (item.mediaType === "IMAGE" && images.length === 0) {
        toast.error("At least one image is required")
        return
      }

      if (item.isCover) {
        toast.error("Set another image as cover before deleting this one")
        return
      }

      const newDeleted = item.uploadId ? [...deletedMediaIds, id] : deletedMediaIds
      setDeletedMediaIds(newDeleted)

      const newItems = mediaItems.filter(i => i.id !== id)
      if (item.src?.startsWith("blob:")) {
        URL.revokeObjectURL(item.src)
      }
      setMediaItems(newItems)
      notifyParent(newItems, newDeleted, thumbnailFiles)
    },
    [mediaItems, deletedMediaIds, thumbnailFiles, notifyParent, onDeleteVideo],
  )

  const handleTitleChange = useCallback(
    (id: string, title: string) => {
      // Route native video title changes to the video pipeline.
      if (id.startsWith(NATIVE_VIDEO_ID_PREFIX)) {
        onNativeVideoTitleChange?.(id.slice(NATIVE_VIDEO_ID_PREFIX.length), title)
        return
      }
      setMediaItems(prev => {
        const updated = prev.map(i => (i.id === id ? { ...i, title, videoTitle: title } : i))
        notifyParent(updated, deletedMediaIds, thumbnailFiles)
        return updated
      })
    },
    [deletedMediaIds, thumbnailFiles, notifyParent, onNativeVideoTitleChange],
  )

  const handleThumbnailSelect = useCallback(
    (id: string) => {
      pendingThumbnailIdRef.current = id
      // For native video cards: always open the file picker (thumbnails are
      // managed through the native video pipeline, not mediaItems).
      if (id.startsWith(NATIVE_VIDEO_ID_PREFIX)) {
        thumbnailInputRef.current?.click()
        return
      }
      const item = mediaItems.find(i => i.id === id)
      if (item?.thumbnailSrc) {
        setEditingThumbnailItem(item)
      } else {
        thumbnailInputRef.current?.click()
      }
    },
    [mediaItems],
  )

  const handleThumbnailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !pendingThumbnailIdRef.current) return

      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file for the thumbnail")
        return
      }
      if (file.size > THUMBNAIL_LIMITS.MAX_FILE_SIZE) {
        const maxMB = THUMBNAIL_LIMITS.MAX_FILE_SIZE / (1024 * 1024)
        toast.error(`Thumbnail image must be less than ${maxMB}MB`)
        return
      }

      const id = pendingThumbnailIdRef.current

      // Route native video thumbnail to the video pipeline.
      if (id.startsWith(NATIVE_VIDEO_ID_PREFIX)) {
        onNativeVideoThumbnailChange?.(id.slice(NATIVE_VIDEO_ID_PREFIX.length), file)
        pendingThumbnailIdRef.current = null
        if (thumbnailInputRef.current) thumbnailInputRef.current.value = ""
        return
      }

      const blobUrl = URL.createObjectURL(file)

      setThumbnailFiles(prev => ({ ...prev, [id]: file }))
      setMediaItems(prev => {
        const updated = prev.map(i =>
          i.id === id ? { ...i, thumbnailSrc: blobUrl, thumbnailFile: file } : i,
        )
        notifyParent(updated, deletedMediaIds, { ...thumbnailFiles, [id]: file })
        return updated
      })

      pendingThumbnailIdRef.current = null
      if (thumbnailInputRef.current) thumbnailInputRef.current.value = ""
    },
    [deletedMediaIds, thumbnailFiles, notifyParent, onNativeVideoThumbnailChange],
  )

  const handleEditImage = useCallback((item: LocalMediaItem) => {
    if (!item.src) return
    setCroppingItem(item)
  }, [])

  const handleCropComplete = useCallback(
    async (croppedFile: File) => {
      if (!croppingItem) return
      const croppedUrl = URL.createObjectURL(croppedFile)

      if (onReplaceImage && croppingItem.uploadId) {
        try {
          const result = await onReplaceImage(croppingItem.uploadId, croppedFile)
          setMediaItems(prev => {
            const updated = prev.map(i =>
              i.id === croppingItem.id
                ? {
                    ...i,
                    uploadId: result?.id ?? i.uploadId,
                    src: result?.filePath ?? croppedUrl,
                    file: undefined,
                  }
                : i,
            )
            notifyParent(updated, deletedMediaIds, thumbnailFiles)
            return updated
          })
        } catch {
          toast.error("Failed to replace image")
        }
        setCroppingItem(null)
        return
      }

      setMediaItems(prev => {
        if (croppingItem.uploadId) {
          const oldId = croppingItem.id
          const newId = `new-${Date.now()}-${Math.random()}`
          const updated = prev.map(i =>
            i.id === oldId
              ? {
                  ...i,
                  id: newId,
                  uploadId: undefined,
                  file: croppedFile,
                  src: croppedUrl,
                }
              : i,
          )
          const newDeleted = [...deletedMediaIds, oldId]
          setDeletedMediaIds(newDeleted)
          notifyParent(updated, newDeleted, thumbnailFiles)
          return updated
        }
        const updated = prev.map(i =>
          i.id === croppingItem.id ? { ...i, file: croppedFile, src: croppedUrl } : i,
        )
        notifyParent(updated, deletedMediaIds, thumbnailFiles)
        return updated
      })
      setCroppingItem(null)
    },
    [croppingItem, deletedMediaIds, thumbnailFiles, notifyParent, onReplaceImage],
  )

  const handleThumbnailCropComplete = useCallback(
    (croppedFile: File) => {
      const id = pendingThumbnailIdRef.current || editingThumbnailItem?.id
      if (!id) return
      const blobUrl = URL.createObjectURL(croppedFile)
      setThumbnailFiles(prev => ({ ...prev, [id]: croppedFile }))
      setMediaItems(prev => {
        const updated = prev.map(i =>
          i.id === id ? { ...i, thumbnailSrc: blobUrl, thumbnailFile: croppedFile } : i,
        )
        notifyParent(updated, deletedMediaIds, { ...thumbnailFiles, [id]: croppedFile })
        return updated
      })
      setEditingThumbnailItem(null)
      pendingThumbnailIdRef.current = null
    },
    [editingThumbnailItem, deletedMediaIds, thumbnailFiles, notifyParent],
  )

  const handleFileUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files) return

      const filesArray = Array.from(files)

      if (isVideoFile && onUploadVideo) {
        for (const file of filesArray.filter(isVideoFile)) {
          void onUploadVideo(file)
        }
      }

      const newItems: LocalMediaItem[] = []

      for (const file of filesArray) {
        if (isVideoFile?.(file) && onUploadVideo) continue

        // Classify with the extension fallback so an Android image/audio/video
        // with an empty/generic MIME isn't misrouted or silently dropped.
        const isImg = looksLikeImageFile(file)
        const isVid = showVideoSection !== false && looksLikeVideoFile(file)
        const isAud = showAudioSection !== false && looksLikeAudioFile(file)

        if (isImg) {
          if (!isFileTypeAllowed(file, allowedImageTypes)) {
            toast.error(`Invalid image type: ${file.name}`)
            continue
          }
          if (file.size > maxImageFileSize) {
            const label =
              maxImageFileSize >= 1024 * 1024 * 1024
                ? `${(maxImageFileSize / (1024 * 1024 * 1024)).toFixed(0)}GB`
                : `${(maxImageFileSize / (1024 * 1024)).toFixed(0)}MB`
            toast.error(`Image "${file.name}" exceeds ${label} limit`)
            continue
          }
        } else if (isVid) {
          if (!isFileTypeAllowed(file, allowedVideoTypes)) {
            toast.error(`Invalid video type: ${file.name}`)
            continue
          }
          if (file.size > maxVideoFileSize) {
            const label =
              maxVideoFileSize >= 1024 * 1024 * 1024
                ? `${(maxVideoFileSize / (1024 * 1024 * 1024)).toFixed(0)}GB`
                : `${(maxVideoFileSize / (1024 * 1024)).toFixed(0)}MB`
            toast.error(`Video "${file.name}" exceeds ${label} limit`)
            continue
          }
        } else if (isAud) {
          if (!isFileTypeAllowed(file, allowedAudioTypes)) {
            toast.error(`Invalid audio type: ${file.name}`)
            continue
          }
          if (file.size > maxAudioFileSize) {
            const label =
              maxAudioFileSize >= 1024 * 1024 * 1024
                ? `${(maxAudioFileSize / (1024 * 1024 * 1024)).toFixed(0)}GB`
                : `${(maxAudioFileSize / (1024 * 1024)).toFixed(0)}MB`
            toast.error(`Audio "${file.name}" exceeds ${label} limit`)
            continue
          }
        } else {
          toast.error(`Unsupported file: ${file.name}`)
          continue
        }

        const blobUrl = URL.createObjectURL(file)
        const mediaType = isImg ? "IMAGE" : isAud ? "AUDIO" : "VIDEO"

        const hasExistingCover = mediaItems.some(i => i.isCover && i.mediaType === "IMAGE")
        const isFirstImageInBatch =
          mediaType === "IMAGE" && !hasExistingCover && !newItems.some(i => i.mediaType === "IMAGE")

        const titleValue = mediaType !== "IMAGE" ? file.name.replace(/\.[^.]+$/, "") : null

        newItems.push({
          id: `new-${Date.now()}-${Math.random()}`,
          src: blobUrl,
          file,
          mediaType,
          isCover: isFirstImageInBatch,
          sortOrder: mediaItems.length + newItems.length,
          title: titleValue,
          videoTitle: titleValue,
        })
      }

      if (newItems.length > 0) {
        setMediaItems(prev => {
          const updated = [...prev, ...newItems]
          notifyParent(updated, deletedMediaIds, thumbnailFiles)
          return updated
        })
      }

      if (fileInputRef.current) fileInputRef.current.value = ""
    },
    [
      mediaItems,
      deletedMediaIds,
      thumbnailFiles,
      notifyParent,
      allowedImageTypes,
      allowedVideoTypes,
      allowedAudioTypes,
      maxImageFileSize,
      maxVideoFileSize,
      maxAudioFileSize,
      showVideoSection,
      showAudioSection,
      isVideoFile,
      onUploadVideo,
    ],
  )

  // Track if handleModalUpload is currently processing to prevent double-execution
  const isProcessingUpload = useRef(false)

  const handleModalUpload = useCallback(
    async (files: File[]) => {
      // Guard against double-execution
      if (isProcessingUpload.current) {
        return
      }
      isProcessingUpload.current = true

      try {
        if (isVideoFile && onUploadVideo) {
          for (const file of files.filter(isVideoFile)) {
            void onUploadVideo(file)
          }
        }

        const nonVideoFiles =
          isVideoFile && onUploadVideo ? files.filter(f => !isVideoFile(f)) : files
        if (nonVideoFiles.length === 0) {
          return
        }

        const newItems: LocalMediaItem[] = []
        const hasExistingCover = mediaItems.some(i => i.isCover && i.mediaType === "IMAGE")

        for (const file of nonVideoFiles) {
          const blobUrl = URL.createObjectURL(file)
          // Extension fallback so an Android audio file with an empty MIME isn't
          // mislabeled as an image (video is filtered into nonVideoFiles above).
          const mediaType = looksLikeAudioFile(file) ? "AUDIO" : "IMAGE"

          const isFirstImageInBatch =
            mediaType === "IMAGE" &&
            !hasExistingCover &&
            !newItems.some(i => i.mediaType === "IMAGE")

          const titleValue = mediaType !== "IMAGE" ? file.name.replace(/\.[^.]+$/, "") : null

          const newItem: LocalMediaItem = {
            id: `new-${Date.now()}-${Math.random()}`,
            src: blobUrl,
            file,
            mediaType: mediaType as "IMAGE" | "VIDEO" | "AUDIO",
            isCover: isFirstImageInBatch,
            sortOrder: mediaItems.length + newItems.length,
            title: titleValue,
            videoTitle: titleValue,
          }
          newItems.push(newItem)
        }

        if (newItems.length > 0) {
          setMediaItems(prev => {
            const updated = [...prev, ...newItems]
            notifyParent(updated, deletedMediaIds, thumbnailFiles)
            return updated
          })
        }
      } finally {
        // Reset the guard after a short delay to allow for legitimate sequential calls
        setTimeout(() => {
          isProcessingUpload.current = false
        }, 100)
      }
    },
    [mediaItems, deletedMediaIds, thumbnailFiles, notifyParent, isVideoFile, onUploadVideo],
  )

  const handleAiImageSelected = useCallback(
    (file: File, _generationId: string, _imageIndex: number) => {
      if (mediaItems.length >= maxFiles) {
        toast.error(`Maximum ${maxFiles} media items already uploaded`)
        return
      }
      const blobUrl = URL.createObjectURL(file)
      const isFirst = mediaItems.length === 0
      const newItem: LocalMediaItem = {
        id: `ai-${Date.now()}-${Math.random()}`,
        src: blobUrl,
        file,
        mediaType: "IMAGE",
        isCover: isFirst,
        sortOrder: mediaItems.length,
        title: null,
        videoTitle: null,
      }
      setMediaItems(prev => {
        const updated = [...prev, newItem]
        notifyParent(updated, deletedMediaIds, thumbnailFiles)
        return updated
      })
      setAiGeneratorOpen(false)
      toast.success("AI-generated image added")
    },
    [mediaItems, deletedMediaIds, thumbnailFiles, maxFiles, notifyParent],
  )

  const handleAudioPosterSelect = useCallback(
    (id: string) => {
      pendingThumbnailIdRef.current = id
      const item = mediaItems.find(i => i.id === id)
      if (item?.thumbnailSrc) {
        setEditingThumbnailItem(item)
      } else {
        audioPosterInputRef.current?.click()
      }
    },
    [mediaItems],
  )

  const handleAudioPosterChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      const id = pendingThumbnailIdRef.current
      if (!file || !id) return

      if (!file.type.startsWith("image/")) {
        toast.error("Audio cover must be an image")
        return
      }
      if (file.size > THUMBNAIL_LIMITS.MAX_FILE_SIZE) {
        const maxMB = THUMBNAIL_LIMITS.MAX_FILE_SIZE / (1024 * 1024)
        toast.error(`Audio cover must be less than ${maxMB}MB`)
        return
      }

      const item = mediaItems.find(i => i.id === id)

      if (onUploadAudioCover && item?.uploadId && ritualId) {
        try {
          await onUploadAudioCover({ ritualId, uploadId: item.uploadId, file })
        } catch {
          toast.error("Failed to update audio cover")
          pendingThumbnailIdRef.current = null
          if (audioPosterInputRef.current) audioPosterInputRef.current.value = ""
          return
        }
      }

      const blobUrl = URL.createObjectURL(file)
      setThumbnailFiles(prev => ({ ...prev, [id]: file }))
      setMediaItems(prev => {
        const updated = prev.map(i =>
          i.id === id ? { ...i, thumbnailSrc: blobUrl, thumbnailFile: file } : i,
        )
        notifyParent(updated, deletedMediaIds, { ...thumbnailFiles, [id]: file })
        return updated
      })

      pendingThumbnailIdRef.current = null
      if (audioPosterInputRef.current) audioPosterInputRef.current.value = ""
    },
    [deletedMediaIds, thumbnailFiles, notifyParent, mediaItems, onUploadAudioCover, ritualId],
  )

  const nativeVideoCards = useMemo<LocalMediaItem[]>(
    () =>
      nativeVideos.map(v => {
        // Bytes, not a rounded percent: a multi-GB upload would read "0%" for
        // minutes. Shared with the ritual cards via nativeVideoStatusSuffix.
        const statusSuffix = nativeVideoStatusSuffix(v)
        const displayTitle = v.title || v.filename
        return {
          id: `${NATIVE_VIDEO_ID_PREFIX}${v.localId}`,
          src: undefined,
          thumbnailSrc: v.thumbnailUrl ?? undefined,
          mediaType: "VIDEO" as const,
          isCover: false,
          sortOrder: v.sortOrder,
          title: `${displayTitle}${statusSuffix}`,
          videoTitle: displayTitle,
        }
      }),
    [nativeVideos],
  )

  const visibleMedia = useMemo(
    () => [...mediaItems.filter(i => !deletedMediaIds.includes(i.id)), ...nativeVideoCards],
    [mediaItems, deletedMediaIds, nativeVideoCards],
  )

  const coverImageUrl = useMemo(() => {
    const coverItem = mediaItems.find(i => i.isCover && !deletedMediaIds.includes(i.id))
    if (coverItem) return coverItem.src
    const firstImage = mediaItems.find(
      i => i.mediaType === "IMAGE" && !deletedMediaIds.includes(i.id),
    )
    return firstImage?.src
  }, [mediaItems, deletedMediaIds])

  const acceptTypes = useMemo(() => {
    const types = [...(allowedImageTypes ?? [])]
    if (showVideoSection !== false) types.push(...(allowedVideoTypes ?? []))
    if (showAudioSection) types.push(...(allowedAudioTypes ?? []))
    return buildAcceptAttribute(types)
  }, [allowedImageTypes, allowedVideoTypes, allowedAudioTypes, showVideoSection, showAudioSection])

  const allAllowedTypes = useMemo(() => {
    const types = [...(allowedImageTypes ?? [])]
    if (showVideoSection !== false) types.push(...(allowedVideoTypes ?? []))
    if (showAudioSection) types.push(...(allowedAudioTypes ?? []))
    return types
  }, [allowedImageTypes, allowedVideoTypes, allowedAudioTypes, showVideoSection, showAudioSection])

  const formatSize = useCallback((bytes: number) => {
    return bytes >= 1024 * 1024 * 1024
      ? `${(bytes / (1024 * 1024 * 1024)).toFixed(0)}GB`
      : `${(bytes / (1024 * 1024)).toFixed(0)}MB`
  }, [])

  const uploadDescriptionText = useMemo(() => {
    let text = `Up to ${maxFiles} files. Images: ${formatSize(maxImageFileSize)}`
    if (showVideoSection !== false) text += `, Videos: ${formatSize(maxVideoFileSize)}`
    if (showAudioSection) text += `, Audio: ${formatSize(maxAudioFileSize)}`
    return text
  }, [
    maxFiles,
    maxImageFileSize,
    maxVideoFileSize,
    maxAudioFileSize,
    showVideoSection,
    showAudioSection,
    formatSize,
  ])

  return (
    <div className="space-y-6">
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptTypes}
        multiple
        onChange={handleFileUpload}
        className="hidden"
        disabled={isUploading ?? false}
      />
      <input
        ref={thumbnailInputRef}
        type="file"
        accept={THUMBNAIL_LIMITS.ALLOWED_TYPES.join(",")}
        onChange={handleThumbnailChange}
        className="hidden"
      />
      {showAudioSection && (
        <input
          ref={audioPosterInputRef}
          type="file"
          accept={THUMBNAIL_LIMITS.ALLOWED_TYPES.join(",")}
          onChange={handleAudioPosterChange}
          className="hidden"
        />
      )}

      <h3 className="font-bold text-primary mb-3.75">Images</h3>

      <div className="flex flex-col md:flex-row md:items-center gap-2.5 md:gap-5">
        <button
          type="button"
          onClick={() => {
            if (visibleMedia.length >= maxFiles) {
              toast.error(`Maximum ${maxFiles} media items already uploaded`)
              return
            }
            setUploadModalOpen(true)
          }}
          className="flex justify-between items-center w-full border border-dashed border-brand-slate-alpha-70 rounded-[15px] p-2.5 pr-5 text-center cursor-pointer bg-water-light-variant hover:bg-accent/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isUploading}
        >
          <div className="flex justify-center items-center gap-2">
            <div className="bg-brand-slate-alpha-70 p-2.5 flex justify-center items-center rounded-full">
              <ArrowUpToLine className="w-5 h-5 text-white" />
            </div>
            <p className="font-semibold text-primary text-sm">Upload Media</p>
          </div>
          <p className="hidden md:block text-xs text-primary leading-5">{uploadDescriptionText}</p>
        </button>

        <p className="md:hidden text-xs text-end text-[#F2F0EB66] leading-5">
          {uploadDescriptionText}
        </p>

        {enableAiGeneration && aiEntityType && (
          <button
            type="button"
            onClick={() => setAiGeneratorOpen(true)}
            className="flex items-center justify-center gap-2.5 border border-[#F2F0EB] md:border-brand-slate-alpha-70 rounded-full px-6.75 py-3 text-center cursor-pointer md:bg-water-light-variant hover:bg-accent/5 transition-colors"
          >
            <AIGeneratedIcon className="w-6 h-6 text-white" />
            <p className="font-semibold text-primary text-sm uppercase text-nowrap">
              Generate Image
            </p>
          </button>
        )}
      </div>

      {visibleMedia.length > 0 && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={visibleMedia.map(i => i.id)} strategy={rectSortingStrategy}>
            <div className="space-y-2">
              <span className="text-xs text-[#F2F0EB66] block mb-5">
                You can drag to reorder images and videos. Cover can only be selected from images.
              </span>
              {visibleMedia.map(item => (
                <SortableMediaItem
                  key={item.id}
                  item={item}
                  onSetCover={handleSetCover}
                  onDelete={handleDelete}
                  onEditImage={handleEditImage}
                  onTitleChange={handleTitleChange}
                  onThumbnailSelect={handleThumbnailSelect}
                  onAudioPosterSelect={showAudioSection ? handleAudioPosterSelect : undefined}
                  coverImageUrl={coverImageUrl}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {enableAiGeneration && aiEntityType && (
        <AiImageGenerator
          open={aiGeneratorOpen}
          onOpenChange={setAiGeneratorOpen}
          entityType={aiEntityType}
          entityContext={aiEntityContext}
          onImageSelected={handleAiImageSelected}
          maxFiles={maxFiles}
        />
      )}

      <MediaUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        onUpload={handleModalUpload}
        maxFiles={maxFiles}
        maxImageFileSize={maxImageFileSize}
        maxVideoFileSize={showVideoSection !== false ? maxVideoFileSize : undefined}
        maxAudioFileSize={showAudioSection ? maxAudioFileSize : undefined}
        allowedTypes={allAllowedTypes}
        title="Upload Media"
        isUploading={isUploading ?? false}
        existingFilesCount={visibleMedia.length}
      />

      <ImageCropperModal
        open={!!croppingItem}
        onOpenChange={open => {
          if (!open) setCroppingItem(null)
        }}
        imageFile={croppingItem?.file ?? null}
        imageUrl={croppingItem?.src ?? null}
        onCropComplete={handleCropComplete}
      />

      <ImageCropperModal
        open={!!editingThumbnailItem}
        onOpenChange={open => {
          if (!open) setEditingThumbnailItem(null)
        }}
        imageFile={editingThumbnailItem?.thumbnailFile ?? null}
        imageUrl={editingThumbnailItem?.thumbnailSrc ?? null}
        onCropComplete={handleThumbnailCropComplete}
      />
    </div>
  )
}
